import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import M_ChatUserRow from '@/components/M_Molecules/M_ChatUserRow/M_ChatUserRow';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { ChatUser, Conversation, chatApi } from '@/services/chatApi';
import { usersApi } from '@/services/usersApi';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import { displayName } from '@/utils/chat';
import styles from './S_NewChatPopup.module.css';

interface S_NewChatPopupProps {
  onClose: () => void;
  onConversationReady: (conversation: Conversation) => void;
}

type Mode = 'direct' | 'group';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 1;

export default function S_NewChatPopup({
  onClose,
  onConversationReady,
}: S_NewChatPopupProps) {
  const { language } = useLanguage();
  const t = useCallback(
    (key: string) => getTranslation(language, key),
    [language]
  );

  const [mode, setMode] = useState<Mode>('direct');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChatUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<ChatUser[]>([]);
  const [groupTitle, setGroupTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      return;
    }

    let isActive = true;
    setIsSearching(true);

    const timeoutId = setTimeout(() => {
      usersApi
        .searchUsers({ q: normalizedQuery, limit: 20 })
        .then(response => {
          if (!isActive) return;
          setResults(
            (response.data?.users || []).map(user => ({
              id: String(user.id),
              name: user.name || '',
              username: user.username || user.handle || '',
              avatar: user.avatar,
            }))
          );
        })
        .catch(() => {
          if (isActive) setResults([]);
        })
        .finally(() => {
          if (isActive) setIsSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [query]);

  const toggleSelected = useCallback((user: ChatUser) => {
    setSelected(previous =>
      previous.some(item => item.id === user.id)
        ? previous.filter(item => item.id !== user.id)
        : [...previous, user]
    );
  }, []);

  const startDirectChat = useCallback(
    async (user: ChatUser) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const response = await chatApi.startDirectConversation(user.id);
        if (response.data?.conversation) {
          onConversationReady(response.data.conversation);
        }
      } catch {
        setError(t('chat.errors.conversationCreate'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [onConversationReady, t]
  );

  const createGroup = useCallback(async () => {
    if (!groupTitle.trim() || selected.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await chatApi.createGroupConversation(
        groupTitle.trim(),
        selected.map(user => user.id)
      );
      if (response.data?.conversation) {
        onConversationReady(response.data.conversation);
      }
    } catch {
      setError(t('chat.errors.conversationCreate'));
    } finally {
      setIsSubmitting(false);
    }
  }, [groupTitle, onConversationReady, selected, t]);

  const canCreateGroup =
    groupTitle.trim().length > 0 && selected.length > 0 && !isSubmitting;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.wrapper}
        onClick={event => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h4 className={styles.title}>
            {mode === 'direct' ? t('chat.new.title') : t('chat.new.groupTitle')}
          </h4>
          <A_CircleButton
            variant='transparent'
            size='small'
            icon={<Q_Icon.CrossIcon width={20} height={20} fill='none' />}
            onClick={onClose}
          />
        </div>

        <div className={styles.tabs}>
          {(['direct', 'group'] as Mode[]).map(value => (
            <button
              key={value}
              type='button'
              className={cn(styles.tab, 'text-p-8', {
                [styles.tab_active]: mode === value,
              })}
              onClick={() => setMode(value)}
            >
              {value === 'direct'
                ? t('chat.new.tabDirect')
                : t('chat.new.tabGroup')}
            </button>
          ))}
        </div>

        {mode === 'group' && (
          <input
            type='text'
            value={groupTitle}
            onChange={event => setGroupTitle(event.target.value)}
            placeholder={t('chat.new.groupNamePlaceholder')}
            className={cn(styles.input, 'text-p-8')}
            maxLength={120}
          />
        )}

        <input
          type='text'
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t('chat.new.searchPlaceholder')}
          className={cn(styles.input, 'text-p-8')}
          autoFocus
        />

        {mode === 'group' && selected.length > 0 && (
          <div className={styles.chips}>
            {selected.map(user => (
              <button
                key={user.id}
                type='button'
                className={cn(styles.chip, 'text-caption')}
                onClick={() => toggleSelected(user)}
              >
                {displayName(user) || user.username}
                <Q_Icon.CrossIcon width={12} height={12} fill='none' />
              </button>
            ))}
          </div>
        )}

        <div className={styles.results}>
          {results.map(user => (
            <M_ChatUserRow
              key={user.id}
              user={user}
              isSelected={selected.some(item => item.id === user.id)}
              onClick={mode === 'direct' ? startDirectChat : toggleSelected}
            />
          ))}

          {results.length === 0 && (
            <p className={cn(styles.hint, 'text-caption')}>
              {isSearching
                ? t('chat.new.searching')
                : query.trim()
                  ? t('chat.new.nothingFound')
                  : t('chat.new.searchHint')}
            </p>
          )}
        </div>

        {error && <p className={cn(styles.error, 'text-caption')}>{error}</p>}

        {mode === 'group' && (
          <A_Button
            variant='primary'
            size='small'
            fontSize='text-p-4'
            width='100%'
            disabled={!canCreateGroup}
            onClick={createGroup}
          >
            {t('chat.new.createGroup')}
          </A_Button>
        )}
      </div>
    </div>
  );
}
